import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RotateCcw, Save, Check, ImageOff, ExternalLink, UploadCloud, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '../../lib/api';
import { IMAGE_SLOTS } from '../../lib/imagery';
import { useBrandImagesMap } from '../../lib/BrandImagesContext';

/**
 * AdminBrandImagesPage — "Brand Shoot Slots"
 *
 * Every stock image on the public site is tagged with a stable slot_id
 * (see IMAGE_SLOTS in lib/imagery.js). Admins can either:
 *   • Drag & drop / pick a file  → uploaded to /api/admin/upload, stored in
 *     Mongo, then the returned URL is saved against the slot, or
 *   • Paste a URL (Unsplash, brand DAM, etc.) and save.
 *
 * Empty URL / Reset = revert to coded default.
 */

const MAX_MB = 6;
const ALLOWED_MIMES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];

// Downscale + recompress on the client so brand photography uploads stay
// under our server-side limit and load quickly on the public site.
async function compressImage(file, { maxDim = 2200, quality = 0.86 } = {}) {
    if (!file.type.startsWith('image/')) return file;
    // Don't attempt to canvas-recompress SVG/GIF — pass through untouched.
    if (file.type === 'image/svg+xml' || file.type === 'image/gif') return file;
    if (file.size < 400 * 1024) return file; // small enough already
    try {
        const bitmap = await createImageBitmap(file);
        const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
        const w = Math.round(bitmap.width * scale);
        const h = Math.round(bitmap.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(bitmap, 0, 0, w, h);
        const outMime = file.type === 'image/png' ? 'image/jpeg' : file.type;
        const blob = await new Promise((res) => canvas.toBlob(res, outMime, quality));
        if (!blob || blob.size >= file.size) return file;
        const ext = outMime === 'image/jpeg' ? 'jpg' : (file.name.split('.').pop() || 'jpg');
        return new File([blob], `${(file.name || 'upload').replace(/\.[^.]+$/, '')}.${ext}`, { type: outMime });
    } catch {
        return file;
    }
}

export default function AdminBrandImagesPage() {
    const { overrides, refresh } = useBrandImagesMap();
    const [drafts, setDrafts] = useState({});
    const [savingId, setSavingId] = useState(null);
    const [uploadingId, setUploadingId] = useState(null);
    const [dragOverId, setDragOverId] = useState(null);
    const [groupFilter, setGroupFilter] = useState('All');
    const fileInputs = useRef({});

    // Sync drafts whenever overrides refresh
    useEffect(() => {
        const next = {};
        for (const s of IMAGE_SLOTS) {
            next[s.id] = overrides[s.id] || '';
        }
        setDrafts(next);
    }, [overrides]);

    const groups = useMemo(() => {
        const set = new Set(['All']);
        for (const s of IMAGE_SLOTS) set.add(s.page);
        return Array.from(set);
    }, []);

    const filtered = useMemo(() => {
        if (groupFilter === 'All') return IMAGE_SLOTS;
        return IMAGE_SLOTS.filter((s) => s.page === groupFilter);
    }, [groupFilter]);

    const save = useCallback(async (slot, url) => {
        try {
            setSavingId(slot.id);
            await api.put('/admin/brand-images', { slot_id: slot.id, url: (url || '').trim() });
            if (!url || !url.trim()) toast.success(`"${slot.label}" reverted to default.`);
            else toast.success(`"${slot.label}" updated.`);
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to save');
        } finally {
            setSavingId(null);
        }
    }, [refresh]);

    const uploadAndAssign = useCallback(async (slot, rawFile) => {
        if (!rawFile) return;
        if (!ALLOWED_MIMES.includes(rawFile.type)) {
            toast.error(`Unsupported file type: ${rawFile.type || 'unknown'}. Use JPG, PNG, WebP, GIF, or SVG.`);
            return;
        }
        if (rawFile.size > MAX_MB * 1024 * 1024 * 3) {
            // We'll compress large files, but reject truly massive ones (>18MB raw)
            toast.error(`File too large. Please keep it under ${MAX_MB * 3} MB before compression.`);
            return;
        }
        try {
            setUploadingId(slot.id);
            const file = await compressImage(rawFile);
            const form = new FormData();
            form.append('file', file);
            const res = await api.post('/admin/upload', form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const url = res?.data?.url;
            if (!url) throw new Error('Upload returned no URL');
            // Update the draft input so the user sees the URL, and save immediately
            setDrafts((prev) => ({ ...prev, [slot.id]: url }));
            await save(slot, url);
        } catch (err) {
            toast.error(err?.response?.data?.detail || err?.message || 'Upload failed');
        } finally {
            setUploadingId(null);
        }
    }, [save]);

    const handleDrop = useCallback((e, slot) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverId(null);
        const file = e.dataTransfer?.files?.[0];
        if (file) uploadAndAssign(slot, file);
    }, [uploadAndAssign]);

    const handlePick = useCallback((e, slot) => {
        const file = e.target?.files?.[0];
        if (file) uploadAndAssign(slot, file);
        // Reset input so picking the same file twice re-triggers
        if (e.target) e.target.value = '';
    }, [uploadAndAssign]);

    const overriddenCount = Object.values(overrides).filter(Boolean).length;

    return (
        <div data-testid="admin-brand-images-page" className="space-y-6">
            <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Brand Images</h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl leading-relaxed">
                        Every stock photo on the public site is registered as a slot. Drop a real brand-photography file
                        (JPG, PNG, WebP, GIF or SVG) onto any slot, or paste a URL — the site will use it immediately with
                        no code change. Leave the URL blank and click <span className="font-mono">Reset</span> to revert to the coded default.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 font-mono uppercase tracking-widest text-[10px]">
                        {overriddenCount} of {IMAGE_SLOTS.length} overridden
                    </Badge>
                </div>
            </header>

            {/* Group filter */}
            <div className="flex flex-wrap gap-2">
                {groups.map((g) => (
                    <button
                        key={g}
                        onClick={() => setGroupFilter(g)}
                        className={`px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-widest border transition-colors ${
                            groupFilter === g
                                ? 'bg-primary/10 border-primary/40 text-primary'
                                : 'border-white/10 text-foreground/75 hover:bg-white/5'
                        }`}
                        data-testid={`brand-group-${g.toLowerCase()}`}
                    >
                        {g}
                    </button>
                ))}
            </div>

            {/* Slots */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {filtered.map((slot) => {
                    const draft = drafts[slot.id] ?? '';
                    const override = overrides[slot.id] || '';
                    const preview = draft.trim() || override || slot.default;
                    const isOverridden = Boolean(override);
                    const isDirty = draft.trim() !== (override || '');
                    const isDragOver = dragOverId === slot.id;
                    const isUploading = uploadingId === slot.id;

                    return (
                        <div
                            key={slot.id}
                            className={`dt-glass rounded-2xl overflow-hidden transition-colors ${isDragOver ? 'border-primary ring-2 ring-primary/40' : ''}`}
                            data-testid={`brand-slot-${slot.id}`}
                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverId(slot.id); }}
                            onDragEnter={(e) => { e.preventDefault(); setDragOverId(slot.id); }}
                            onDragLeave={(e) => { e.preventDefault(); if (dragOverId === slot.id) setDragOverId(null); }}
                            onDrop={(e) => handleDrop(e, slot)}
                        >
                            <div className="grid grid-cols-3">
                                {/* Preview / drop zone */}
                                <div
                                    className={`col-span-1 relative aspect-[4/3] bg-white/5 overflow-hidden border-r border-white/10 cursor-pointer group ${isDragOver ? 'bg-primary/10' : ''}`}
                                    onClick={() => fileInputs.current[slot.id]?.click()}
                                    role="button"
                                    aria-label={`Upload image for ${slot.label}`}
                                >
                                    {preview ? (
                                        <img
                                            src={preview}
                                            alt=""
                                            aria-hidden
                                            loading="lazy"
                                            className="w-full h-full object-cover transition-opacity group-hover:opacity-50"
                                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                            <ImageOff className="h-6 w-6" />
                                        </div>
                                    )}
                                    {/* Hover / drag overlay */}
                                    <div className={`absolute inset-0 flex flex-col items-center justify-center gap-1 text-primary transition-opacity ${isDragOver || isUploading ? 'opacity-100 bg-[rgba(8,12,20,0.75)]' : 'opacity-0 group-hover:opacity-100 group-hover:bg-[rgba(8,12,20,0.6)]'}`}>
                                        {isUploading ? (
                                            <>
                                                <Loader2 className="h-5 w-5 animate-spin" />
                                                <div className="text-[10px] font-mono uppercase tracking-widest">Uploading…</div>
                                            </>
                                        ) : (
                                            <>
                                                <UploadCloud className="h-5 w-5" />
                                                <div className="text-[10px] font-mono uppercase tracking-widest">
                                                    {isDragOver ? 'Drop to upload' : 'Drag or click'}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    {isOverridden && (
                                        <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest bg-primary/15 text-primary border border-primary/40 px-2 py-0.5 rounded-full">
                                            <Check className="h-3 w-3" /> Brand
                                        </span>
                                    )}
                                    <input
                                        ref={(el) => { fileInputs.current[slot.id] = el; }}
                                        type="file"
                                        accept={ALLOWED_MIMES.join(',')}
                                        className="hidden"
                                        onChange={(e) => handlePick(e, slot)}
                                        data-testid={`brand-file-${slot.id}`}
                                    />
                                </div>

                                {/* Meta + input */}
                                <div className="col-span-2 p-4 space-y-3">
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <Badge variant="outline" className="border-white/15 text-foreground/70 font-mono text-[10px] tracking-widest uppercase">
                                                    {slot.page}
                                                </Badge>
                                                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{slot.id}</span>
                                            </div>
                                            <div className="mt-1 font-display text-base font-semibold">{slot.label}</div>
                                            <div className="text-xs text-muted-foreground leading-relaxed mt-1">{slot.description}</div>
                                        </div>
                                    </div>

                                    <div className="space-y-1.5">
                                        <div className="flex items-center justify-between">
                                            <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70">
                                                Brand photography URL
                                            </Label>
                                            <button
                                                type="button"
                                                onClick={() => fileInputs.current[slot.id]?.click()}
                                                className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-primary hover:text-accent"
                                                data-testid={`brand-upload-btn-${slot.id}`}
                                                disabled={isUploading}
                                            >
                                                <UploadCloud className="h-3 w-3" />
                                                {isUploading ? 'Uploading…' : 'Upload file'}
                                            </button>
                                        </div>
                                        <Input
                                            value={draft}
                                            onChange={(e) => setDrafts((prev) => ({ ...prev, [slot.id]: e.target.value }))}
                                            placeholder={slot.default || 'https://…'}
                                            className="bg-white/5 border-white/10 font-mono text-xs"
                                            data-testid={`brand-input-${slot.id}`}
                                            disabled={isUploading}
                                        />
                                        <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-muted-foreground pt-1">
                                            <a
                                                href={slot.default}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center gap-1 hover:text-primary"
                                            >
                                                Default <ExternalLink className="h-3 w-3" />
                                            </a>
                                            {isOverridden ? (
                                                <span className="text-primary">Override active</span>
                                            ) : (
                                                <span>Using default</span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-end gap-2 pt-1">
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => save(slot, '')}
                                            disabled={savingId === slot.id || isUploading || !isOverridden}
                                            data-testid={`brand-reset-${slot.id}`}
                                            className="text-foreground/80"
                                        >
                                            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset
                                        </Button>
                                        <Button
                                            size="sm"
                                            onClick={() => save(slot, draft)}
                                            disabled={savingId === slot.id || isUploading || !isDirty}
                                            data-testid={`brand-save-${slot.id}`}
                                            className="bg-primary text-primary-foreground hover:bg-primary/90"
                                        >
                                            <Save className="h-3.5 w-3.5 mr-1" />
                                            {savingId === slot.id ? 'Saving…' : 'Save'}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
